import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from 'context/AuthContext';
import { CartProvider } from 'context/CartContext';
import { ProductProvider } from 'context/ProductContext';
import Layout from 'components/layout/Layout';
import ErrorBoundary from 'components/layout/ErrorBoundary';
import ProtectedRoute from 'components/layout/ProtectedRoute';
import IntroLoginMerge from './IntroLoginMerge';

// Initialize Sentry lazily — does not block initial render
import('config/sentry').then(({ initSentry }) => initSentry());

// Capture acquisition source on first page load (STORY-01.2.07). Runs once
// per session-start; idempotent thereafter (first-touch wins for 30 days).
import('utils/sessionSource').then(({ captureSessionSource }) => {
  try { captureSessionSource(); } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sessionSource] capture failed (non-blocking):', err);
  }
});

// Lazy load pages for code splitting.
// Each dynamic import carries a webpackChunkName magic comment so the build
// emits human-named chunks (e.g. `admin-shell.<hash>.chunk.js`). This lets
// scripts/check-bundle-size.js apply per-chunk budgets — the admin shell gets
// the looser PRD §9 allowance, every storefront page chunk gets the tighter
// per-page budget.
const Home = lazy(() => import(/* webpackChunkName: "home" */ '../pages/Home'));
const Products = lazy(() => import(/* webpackChunkName: "products" */ '../pages/Products'));
const ProductDetail = lazy(() => import(/* webpackChunkName: "product-detail" */ '../pages/ProductDetail'));
const Cart = lazy(() => import(/* webpackChunkName: "cart" */ '../pages/Cart'));
const Checkout = lazy(() => import(/* webpackChunkName: "checkout" */ '../pages/Checkout'));
const OrderSuccess = lazy(() => import(/* webpackChunkName: "order-success" */ '../pages/OrderSuccess'));
const About = lazy(() => import(/* webpackChunkName: "about" */ '../pages/About'));
const Contact = lazy(() => import(/* webpackChunkName: "contact" */ '../pages/Contact'));
const Login = lazy(() => import(/* webpackChunkName: "login" */ '../pages/Login'));
const Signup = lazy(() => import(/* webpackChunkName: "signup" */ '../pages/Signup'));
const ForgotPassword = lazy(() => import(/* webpackChunkName: "forgot-password" */ '../pages/ForgotPassword'));
const Account = lazy(() => import(/* webpackChunkName: "account" */ '../pages/Account'));
const Unsubscribe = lazy(() => import(/* webpackChunkName: "unsubscribe" */ '../pages/Unsubscribe'));
const InvoiceView = lazy(() => import(/* webpackChunkName: "invoice-view" */ '../pages/InvoiceView'));
// New admin — EPIC-00 onward. Mounted at /admin/*. The chunk name is
// load-bearing: scripts/check-bundle-size.js matches `admin-shell.*.chunk.js`
// to a looser budget (PRD §9: admin shell ≤120 KB gz) than per-page chunks.
const AdminApp = lazy(() => import(/* webpackChunkName: "admin-shell" */ '../admin/AdminApp'));
// Design preview kept for /admin-preview/* during the build so we can keep
// comparing the spec to the real implementation. Remove when the new admin
// is fully shipped.
const AdminPreview = lazy(() => import(/* webpackChunkName: "admin-preview" */ '../admin-preview/AdminPreview'));

// Component fixture routes — only mounted when NODE_ENV !== 'production'.
// Used by Playwright responsive specs (STORY-13.5.03 TC-06) to render
// admin slide-overs without going through auth + Firestore.
const FIXTURES_ENABLED = process.env.NODE_ENV !== 'production';
const AssetSlideOverFixture = FIXTURES_ENABLED
  ? lazy(() => import(/* webpackChunkName: "fixtures-asset-slideover" */ '../__fixtures__/AssetSlideOverFixture'))
  : null;
const NotFound = lazy(() => import(/* webpackChunkName: "not-found" */ '../pages/NotFound'));
// EPIC-09 curtain-raise intro at /intro. Lives outside StorefrontLayout
// (no Header / Footer / AnnouncementBar) — owns the viewport.
const Intro = lazy(() => import(/* webpackChunkName: "intro" */ '../components/intro/Intro'));
// Render-error boundary specific to the intro (STORY-09.4.05). Silently
// redirects to Home + counts the broken play + reports to Sentry.
const IntroErrorBoundary = lazy(() => import(/* webpackChunkName: "intro-error" */ '../components/intro/IntroErrorBoundary'));

// Loading component
// `minHeight: 100vh` is load-bearing: during the brief route-chunk
// fetch, the Suspense fallback replaces the page body. With a small
// fallback the Footer (which sits below `<Outlet />` in `<Layout>`)
// renders in the viewport, then gets shoved off-screen when the real
// page loads — confirmed as a ~0.27 CLS spike at ~1.2s in the perf
// audit (tests/home-perf-audit.spec.js). 100vh keeps the footer below
// the viewport during loading, so the move into final position is
// not visible (and not counted by the layout-shift API).
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: '1.2rem',
    color: 'var(--text-secondary)'
  }}>
    Loading...
  </div>
);

// Storefront layout route — wraps every public-facing page in <Layout> +
// ErrorBoundary + Suspense via <Outlet>. Replaces the v6-era pattern of
// nesting a second <Routes> inside a splat route, which v7 resolves
// differently and broke storefront navigation after the upgrade.
const StorefrontLayout = () => (
  <Layout>
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  </Layout>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ProductProvider>
          <CartProvider>
            <Router>
              {/* EPIC-09: reconcile local visit cache with Firestore profile
                  on anonymous → logged-in transitions. Renders nothing. */}
              <IntroLoginMerge />
              <Routes>
                {/* New admin (EPIC-00 onward) — runs outside storefront Layout
                    so it renders its own sidebar/topbar. Auth gating is
                    handled inside AdminApp via AdminPrivateRoute + custom
                    claims (STORY-00.2.02/03). */}
                <Route
                  path="/admin/*"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminApp />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                {/* Design preview — kept while we build for spec comparison.
                    Remove once /admin reaches full parity. */}
                <Route
                  path="/admin-preview/*"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminPreview />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />

                {/* Component fixture routes — dev/test only. Skipped in
                 *  production builds via the FIXTURES_ENABLED guard. */}
                {FIXTURES_ENABLED && AssetSlideOverFixture && (
                  <Route
                    path="/__fixtures/asset-slideover"
                    element={
                      <ErrorBoundary>
                        <Suspense fallback={<LoadingFallback />}>
                          <AssetSlideOverFixture />
                        </Suspense>
                      </ErrorBoundary>
                    }
                  />
                )}

                {/* /intro — curtain-raise overlay (EPIC-09). Outside the
                    storefront Layout. Exact path: /intro/anything 404s through
                    the storefront splat. Two error boundaries:
                      - outer <ErrorBoundary>: catches lazy-load failures
                      - inner <IntroErrorBoundary>: catches render errors and
                        silently redirects to Home (STORY-09.4.05) */}
                <Route
                  path="/intro"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingFallback />}>
                        <IntroErrorBoundary>
                          <Intro />
                        </IntroErrorBoundary>
                      </Suspense>
                    </ErrorBoundary>
                  }
                />

                {/* Storefront — every public page renders through StorefrontLayout
                    via <Outlet>. Single flat Routes block (v7-idiomatic). */}
                <Route element={<StorefrontLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-success" element={<OrderSuccess />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                  {/* Public — no auth. EPIC-06 FEAT-06.5. */}
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  {/* EPIC-10 FEAT-10.5 — customer-facing invoice view via HMAC token. */}
                  <Route path="/invoices/view/:id" element={<InvoiceView />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Router>
          </CartProvider>
        </ProductProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;

